import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useRecoilState } from "recoil";
import { notification, PageHeader, Skeleton, Space, Tabs } from "antd";
import { getEnvironmentService, getKubeService } from "../../backend";
import DeploymentStatusTag, { isResourceDeployed, mapDeploymentStatus } from "../../components/DeploymentStatusTag";
import PageContentWrapper from "../../components/PageContentWrapper";
import { currentProjectState } from "../../recoil/project";
import { IEnvDetails } from "../../types";
import { ItemType } from "antd/lib/menu/hooks/useItems";
import ConnectAwsAccountModal from "../../components/settings/ConnectAwsAccountModal";
import AwsLogo from "../../assets/aws-logo.svg";
import { AWS_REGIONS } from "../../enums/enums";
import PageHeaderContent from "../../components/PageHeaderContent";
import PageHeaderBreadcrumb from "../../components/PageHeaderBreadcrumb";
import { fetchProjectLimitations } from "../../utils/local-storage";
// import { showRequestClusterConfirm } from "../../components/DemoClusterModal";
import { handleDeployEnvironment } from "../../utils/environment/handle-deploy-environment";
// import { useCurrentProject } from "../../contexts/Project";
import { trackQuickDeploy } from "../../backend/tracking/deployment";
import { handleUndeployEnvironment, showUndeployEnvironmentModalConfirm } from "../../utils/environment/handle-undeploy-environment";
import { useDoubleConfigurationModal } from "../../contexts/Modal";
// import { ListKubernetesResponseKubernetesesTypeEnum } from "@microtica/ms-kube-sdk";
import MenuItemLabel from "../../components/MenuItemLabel";
import { fetchAwsAccountsInfo, linkAwsAccountToStage } from "../../utils/aws-accounts";
import ChooseAwsAccountModal from "../../components/modals/ChooseAwsAccountModal";
import { useCurrentProject } from "../../contexts/Project";

const getSummaryItem = (total: number, status: string, entity: "App" | "Resource") => {
    return total === 0 ? [] :
        [{ secondaryText: `${total} ${status} ${total === 1 ? entity : `${entity}s`}` }]
}

const EnvironmentRoot = () => {
    const { projectId, envId } = useParams();
    const { deploymentId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [currentProject, setCurrentProject] = useRecoilState(currentProjectState);
    const [activeTab, setActiveTab] = useState("apps");
    const [envDetails, setEnvDetails] = useState<IEnvDetails>();
    const [connectAwsAccountModalVisible, setConnectAwsAccountModalVisible] = useState(false);
    // const [projectHasSharedCluster, setProjectHasSharedCluster] = useState<boolean>(false);
    // const [shouldInitiateTrial, setShouldInitiateTrial] = useState(currentProject?.paymentPlan?.id === "FREE");
    const { kubernetesServices: kubernetesServicesLimitation } = fetchProjectLimitations();
    const { open: openConfirmModal } = useDoubleConfigurationModal();
    // const { updateCurrentProject } = useCurrentProject();
    const [chooseAwsAccountModal, setChooseAwsAccountModal] = useState(false);
    const [awsAccountId, setAwsAccountId] = useState<string>();
    const [notFoundError, setNotFoundError] = useState<boolean>(false);
    const { updateCurrentProject } = useCurrentProject();

    useEffect(() => {
        // change the current project if the projectId in the URL is different from the current project saved in local storage
        // this is the root component for all Environment related routes, so no need to do this in other places
        if (!!projectId && projectId !== currentProject.id) {
            updateCurrentProject(projectId);
        }
    }, [projectId])

    useEffect(() => {
        loadData();
        const interval = setInterval(async () => {
            await loadData();
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        setActiveTab(location.pathname.split("/")[5] || "apps");
    }, [location.pathname]);


    const shouldDisableUndeployAction = (status: string) => {
        return ["NOT_DEPLOYED", "REVIEW_IN_PROGRESS", "DELETE_COMPLETE"].includes(status) || status.endsWith("IN_PROGRESS");
    }

    const loadData = async () => {
        const [
            { data: env }, { data: { deployedMicroservices } }, { data: { resources } },
            // { data: { kuberneteses } }
        ] = await Promise.all([
            getEnvironmentService().getStage(envId!, projectId!)
                .catch(err => {
                    // display PageNotFound if this API call failed with 404 error
                    if (err?.response?.data?.code === 404) {
                        setNotFoundError(true);
                    }
                    throw err;
                }),
            getKubeService().getDeployedMicroservicesInStage(envId!, projectId!),
            getEnvironmentService().getStageResources(envId!, projectId!),
            // getKubeService().listKubernetes(projectId!)
        ]);

        setNotFoundError(false);
        const allServices = deployedMicroservices.flatMap(d => d.microservices);
        setEnvDetails({
            id: env.id,
            name: env.name,
            description: env.description,
            lastDeployed: env.lastDeployed,
            status: env.status,
            awsAccountId: env.awsAccountId,
            region: env.region,
            infrastructureAsCodeTool: env.infrastructureAsCodeTool,
            cloudProvider: env.cloudProvider,
            lastDeploymentId: env.lastDeploymentId,
            isUndeployDisabled: shouldDisableUndeployAction(env.status || ""),
            totalMicroservices: allServices.length,
            totalHealthyMicroservices: allServices.filter(ms => ms.isHealthy).length,
            totalFailedMicroservices: allServices.filter(ms => !ms.isHealthy && !ms.isPending).length,
            totalDeployedResources: resources.filter(r => isResourceDeployed(r.status)).length,
            totalResources: resources.length
        });
        // setProjectHasSharedCluster(!!kuberneteses.find(kube => kube.type === ListKubernetesResponseKubernetesesTypeEnum.Shared))
    }

    const deployStage = async () => {
        try {
            const { stageAwsAccountId, projectAwsAccounts } = await fetchAwsAccountsInfo(envId!, projectId!);

            if (stageAwsAccountId) {
                // Check if the env needs to be undeployed first
                if (envDetails?.status === "REVIEW_IN_PROGRESS" || envDetails?.status === "ROLLBACK_COMPLETE") {
                    showUndeployEnvironmentModalConfirm({
                        envId: envId!,
                        envStatus: envDetails?.status,
                        projectId: projectId!,
                        navigate
                    });
                } else {
                    // AWS account is connected to both stage and project
                    await getEnvironmentService().deployStage(envId!, projectId!);
                    notification.info({
                        message: "Deployment in progress",
                        description: "New deployment of the infrastructure was triggered."
                    });
                    trackQuickDeploy(envId!);
                    navigate(`/projects/${projectId}/environments/${envId}/deployments`);
                }
            } else {
                if (!!projectAwsAccounts.length) {
                    // project has AWS accounts, but none of them is connected to the stage
                    setChooseAwsAccountModal(true);
                } else {
                    // there is no AWS account on the project
                    setConnectAwsAccountModalVisible(true);
                }
            }
        } catch (err: any) {
            notification.error({
                message: "Error occured",
                description: err.response.data ? err.response.data.message : "Unknown error occured"
            });
        }
    }

    const headerBreadcrumb = envDetails ?
        <PageHeaderBreadcrumb
            items={[
                { link: "/projects", name: "Projects" },
                { link: `/projects/${projectId!}`, name: currentProject.name },
                { link: `/projects/${projectId!}/environments`, name: "Environments" },
                { link: `/projects/${projectId!}/environments/${envId}`, name: envDetails.name },
                ...deploymentId ? [
                    { link: `/projects/${projectId!}/environments/${envId}/deployments`, name: "Deployments" },
                    { link: `/projects/${projectId!}/environments/${envId}/deployments/${deploymentId}`, name: deploymentId }
                ] : []
            ]}
        /> : <Skeleton.Button active={true} size="small" />;

    const headerTabs = (
        <Tabs
            activeKey={activeTab}
            onChange={(activeKey) => {
                navigate(activeKey);
                setActiveTab(activeKey)
            }}
        >
            <Tabs.TabPane tab="Apps" key="apps" />
            <Tabs.TabPane tab="Resources" key="infrastructure" />
            <Tabs.TabPane tab={
                <Space>
                    Deployments
                    {
                        activeTab !== "deployments" && envDetails?.status?.endsWith("_IN_PROGRESS") &&
                        <DeploymentStatusTag status={envDetails?.status} />
                    }
                </Space>
            } key="deployments" />
            <Tabs.TabPane tab="Templates" key="active-templates" />
            <Tabs.TabPane tab="Environment Settings" key="settings" />
        </Tabs>
    );

    const actionItems = [
        {
            label: <MenuItemLabel
                text="Add App"
                showTooltip={kubernetesServicesLimitation <= (envDetails?.totalMicroservices || 0)}
                tooltipText={
                    <>
                        Reached limit of {kubernetesServicesLimitation} apps.
                        <Link to="/pricing/upgrade" target="_blank">&nbsp;Upgrade plan</Link>
                    </>
                }
            />,
            key: "app",
            onClick: () => navigate(`/projects/${projectId}/environments/${envId}/apps/new`),
            disabled: kubernetesServicesLimitation <= (envDetails?.totalMicroservices || 0)
        },
        {
            label: "Create Cluster",
            key: "cluster",
            children: [
                {
                    label: "Amazon EKS",
                    key: "eks",
                    onClick: () => navigate(
                        "/templates/new?template=https://raw.githubusercontent.com/microtica/templates/master/aws-eks/template.yaml",
                        { state: { envId } }
                    )
                },
                {
                    label: "Amazon EKS Spot",
                    key: "eks-spot",
                    onClick: () => navigate(
                        "/templates/new?template=https://raw.githubusercontent.com/microtica/templates/master/aws-eks-spot/template.yaml",
                        { state: { envId } }
                    )
                },
                // {
                //     label: <MenuItemLabel
                //         text="Free Demo Cluster"
                //         showTooltip={projectHasSharedCluster}
                //         tooltipText="Demo cluster already exists in your project."
                //     />,
                //     key: "demo-cluster",
                //     disabled: projectHasSharedCluster,
                //     onClick: () => { showRequestClusterConfirm(currentProject, envId); }
                // }
            ],
        },
        { type: "divider" },
        {
            label: "Add Resource",
            key: "resource",
            onClick: () => navigate(`/projects/${projectId}/environments/${envId}/infrastructure/resources`)
        },
        {
            label: "Connect AWS Account",
            key: "aws",
            onClick: () => setConnectAwsAccountModalVisible(true)
        },
        { type: "divider" },
        {
            label: <MenuItemLabel
                text="Deploy All Resources"
                showTooltip={envDetails?.status?.endsWith("IN_PROGRESS") || !envDetails?.totalResources}
                tooltipText={envDetails?.status?.endsWith("IN_PROGRESS") ? `Environment status is "${mapDeploymentStatus(envDetails?.status).status}"` : "There are no resources in your environment."}
            />,
            key: "deploy",
            disabled: envDetails?.status?.endsWith("IN_PROGRESS") || !envDetails?.totalResources,
            onClick: () => handleDeployEnvironment({
                // currentProjectId: projectId!,
                // shouldInitiateTrial,
                // setShouldInitiateTrial,
                deployStage,
                // setCurrentProject,
                // updateCurrentProject
            })
        },
        {
            label: <MenuItemLabel
                text="Undeploy Resources"
                showTooltip={!!envDetails?.isUndeployDisabled || !envDetails?.awsAccountId}
                tooltipText={!envDetails?.awsAccountId ?
                    "No connected AWS account" :
                    `Environment status is "${mapDeploymentStatus(envDetails?.status).status}".`}
            />,
            key: "undeploy",
            danger: !(envDetails?.isUndeployDisabled || !envDetails?.awsAccountId),
            disabled: envDetails?.isUndeployDisabled || !envDetails?.awsAccountId,
            onClick: () => openConfirmModal({
                title: "Undeploy Infrastructure?",
                description: "The infrastructure resources in the environment will be permanently deleted from your cloud account.",
                confirmation: envId!,
                alert: <b>This action cannot be undone.</b>,
                cancelText: "Cancel",
                okText: "Undeploy Infrastructure",
                onOk: () => handleUndeployEnvironment({
                    envId: envId!,
                    projectId: projectId!,
                    navigate
                })
            })
        }
    ] as ItemType[];

    const headerContent = envDetails ?
        <PageHeaderContent
            title={{
                prefix: "Environment",
                mainText: envDetails.name
            }}
            actionItems={actionItems}
            summary={[
                {
                    primaryText: <>
                        <img src={AwsLogo} style={{ width: "20px" }} alt="aws" />&nbsp;
                        {envDetails.awsAccountId || <>No connected AWS account</>}
                    </>,
                    secondaryText: envDetails.awsAccountId && envDetails.region ?
                        <>-&nbsp;{AWS_REGIONS.find(region => region.value === envDetails.region || region.id === envDetails.region)?.name}</>
                        : undefined
                },
                ...getSummaryItem(envDetails.totalHealthyMicroservices, "Active", "App"),
                ...getSummaryItem(envDetails.totalFailedMicroservices, "Failed", "App"),
                ...getSummaryItem(envDetails.totalDeployedResources, "Deployed", "Resource")
            ]}
        /> : <Skeleton.Input />

    return (
        <PageContentWrapper
            size="large"
            header={
                <PageHeader
                    title={headerBreadcrumb}
                    footer={headerTabs}
                    style={{ paddingTop: "8px" }}
                >
                    {headerContent}
                </PageHeader>
            }
            loading={!envDetails}
            notFoundError={notFoundError}
        >
            {envDetails ? <Outlet context={{ envDetails }} /> : <Skeleton />}
            {
                // Workaround. The modal is not unmouting when closed so we need to unmount the whole component using 'modalVisible'
                // We have a process of periodic API calls which remain active even if when the modal closes
                connectAwsAccountModalVisible &&
                <ConnectAwsAccountModal
                    visible={connectAwsAccountModalVisible}
                    onOk={async (newAwsAccountId) => {
                        setConnectAwsAccountModalVisible(false);
                        setAwsAccountId(newAwsAccountId);
                        setChooseAwsAccountModal(true);
                        loadData();
                    }}
                    onCancel={() => setConnectAwsAccountModalVisible(false)}
                />
            }
            {
                // This modal becomes active when the user connects an AWS account and now needs to select a region for the stage
                chooseAwsAccountModal &&
                <ChooseAwsAccountModal
                    selectedAccount={{ accountId: awsAccountId, region: undefined }}
                    visible={chooseAwsAccountModal}
                    onCancel={() => setChooseAwsAccountModal(false)}
                    onOk={async ({ accountId, region }: { accountId: string, region: string }) => {
                        await linkAwsAccountToStage({
                            envId: envId!,
                            projectId: projectId!,
                            awsAccountId: accountId,
                            awsRegion: region
                        });
                        setChooseAwsAccountModal(false);
                        loadData();
                    }}
                />
            }
        </PageContentWrapper>
    );
}

export default EnvironmentRoot;